import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import type { HospitalData, Nurse, NurseInShift } from "../../utils/models";
import {
	groupNurseShiftsForDay,
	type QuickAddConflict,
	QuickAddConflictKind,
	resolveQuickAddConflict,
	type ShiftWindow,
	singleBlockingShift,
} from "../../utils/scheduleConflict";
import { buildConflictHint } from "../../utils/scheduleConflictCopy";

const NO_CONFLICT: QuickAddConflict = { kind: QuickAddConflictKind.None };

/** The day's scheduled shifts by person; empty in actual mode, where the server refuses no overlap to predict. */
const dayShiftsByNurse = (weeks: HospitalData["weeks"], weekIndex: number, dayIndex: number, showActual: boolean): Map<number, NurseInShift[]> => {
	if (showActual) return new Map();
	// `dayIndex` is a position in the week's first row — how `getDayIndexByDate` defines it — so that row's day
	// gives the date every row is matched by.
	const week = weeks[weekIndex];
	const dayDate = week?.weeklyShifts[0]?.days[dayIndex]?.date;
	// DEFAULT: a day that isn't loaded has nothing to predict against. Not reached with the "+" on screen: the Day
	// Schedule renders nothing while its date's week/day index is -1 (`DaySchedule`), and both the table and the
	// gaps layout take their indexes from the same `getWeekIndexByDate` / `getDayIndexByDate` on the same date.
	// If it ever were, the server still refuses the overlap (409) and the add's error snackbar says so.
	if (!week || !dayDate) return new Map();
	return groupNurseShiftsForDay(week.weeklyShifts, dayDate);
};

type Params = {
	weekIndex: number;
	dayIndex: number;
	showActual: boolean;
	nurse: Nurse | null;
	window: ShiftWindow;
	/** The destination for the shift the current times clash with (`undefined` when none or several do). */
	locationFor: (blockingShift: NurseInShift | undefined) => string | null;
};

/**
 * What the "+" Add Staff dialog should say and allow for the picked person, given who is already on the day's
 * schedule: the day's shifts by person (which the staff picker lists one row per shift from), and the
 * destination and intent (add / double / move / blocked) for the current times.
 */
export const useQuickAddScheduleConflict = ({ weekIndex, dayIndex, showActual, nurse, window, locationFor }: Params) => {
	const { t } = useTranslation();
	const { hospitalData, timezone } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	const shiftsByNurse = useMemo(
		() => dayShiftsByNurse(hospitalData.weeks, weekIndex, dayIndex, showActual),
		[hospitalData.weeks, weekIndex, dayIndex, showActual],
	);
	// DEFAULT: no entry in the day's map means the person has no shift that day — a plain add, nothing to predict.
	const sameDayShifts = nurse ? shiftsByNurse.get(nurse.id) : undefined;
	// The clash is found before the destination (it does not depend on one), so the destination can follow it.
	const blockingShift = useMemo(() => (sameDayShifts ? singleBlockingShift(sameDayShifts, window) : undefined), [sameDayShifts, window]);
	const location = locationFor(blockingShift);
	const conflict = useMemo<QuickAddConflict>(
		() => (sameDayShifts ? resolveQuickAddConflict({ sameDayShifts, window, location }) : NO_CONFLICT),
		[sameDayShifts, window, location],
	);

	const hint = useMemo(
		() => (nurse ? buildConflictHint({ conflict, name: formatPersonName(nurse), location, t, timezone }) : null),
		[conflict, nurse, location, t, timezone, formatPersonName],
	);
	const isBlocked = conflict.kind === QuickAddConflictKind.SameLocation || conflict.kind === QuickAddConflictKind.MultipleOverlap;
	return { shiftsByNurse, location, conflict, hint, isBlocked };
};
